import Chunk from "../game/chunk";
import GameMap from "../game/gameMap";
import Edge from "./edge";
import Graph from "./graph";
import PathNode from "./pathNode";
import { AreaNode } from "../types";
import { pointInPolygon, lineIntersectsPolygon, polygonBounds } from "../geometry";

class PathAi {
    nodes: PathNode[] = [];
    margin: number;
    path: PathNode[] = [];
    // Absolute-world-space polygons for every collidable ("wall"-type) area near
    // the current path query. Damage/trigger/portal areas don't block movement,
    // same as player collision, so they're excluded here.
    obstacles: AreaNode[][] = [];
    edges: Edge[] = [];
    graph: Graph;

    constructor(private gameMap: GameMap, margin: number = 100) {
        this.margin = margin;
    }

    calculatePath(startX: number, startY: number, endX: number, endY: number) {
        this.nodes = [];
        this.path = [];
        this.obstacles = [];
        this.edges = [];

        this.nodes.push(new PathNode(startX, startY));
        this.nodes.push(new PathNode(endX, endY));

        //update obstacles from chunks
        this.updateObstaclesFromChunks(startX, startY, endX, endY);

        if(!this.lineIntersectsObstacles(startX, startY, endX, endY)){
            this.path = this.nodes;
            return;
        }

        //generate node cloud
        this.generateNodeCloud();

        this.connectNodes(1000);

        // Create a graph from the edges
        this.graph = new Graph(this.edges);

        // Find the shortest path
        this.path = this.dijkstra(this.graph, this.nodes[0], this.nodes[1]) || [];
    }

    updateObstaclesFromChunks(x1: number, y1: number, x2: number, y2: number) {
        // Get collidable areas from chunks of interest, converted to absolute
        // world-space polygons.

        let cx1 = Math.floor(x1 / Chunk.CHUNK_SIZE);
        let cy1 = Math.floor(y1 / Chunk.CHUNK_SIZE);
        let cx2 = Math.floor(x2 / Chunk.CHUNK_SIZE);
        let cy2 = Math.floor(y2 / Chunk.CHUNK_SIZE);

        //make sure that the cx1 and cy2 is smaller than cx2 and cy2
        if(cx1 > cx2){
            let tmp = cx1;
            cx1 = cx2;
            cx2 = tmp;
        }
        if(cy1 > cy2){
            let tmp = cy1;
            cy1 = cy2;
            cy2 = tmp;
        }


        this.obstacles = [];
        for(let x = cx1-1; x <= cx2+1; x++){
            for(let y = cy1-1; y <= cy2+1; y++){
                let chunk = this.gameMap.getChunk(x, y);

                if(!chunk){
                    console.log('chunk not found');
                    continue;
                }

                for(let area of chunk.data.areas){
                    if(area.nodes.length < 3){
                        continue;
                    }
                    // Only solid "wall" (or legacy-unspecified) areas block pathing -
                    // damage/trigger/portal areas don't physically block the player
                    // either, so they shouldn't block pathfinding.
                    if(area.type && area.type !== "wall"){
                        continue;
                    }
                    let polygon = area.nodes.map(n => ({x: n.x + x * Chunk.CHUNK_SIZE, y: n.y + y * Chunk.CHUNK_SIZE}));
                    this.obstacles.push(polygon);
                }
            }
        }
    }

    generateNodeCloud() {
        // Add nodes on the corners of each obstacle's bounding box (expanded by
        // margin), not its exact polygon vertices. This is a simplification:
        // for concave "area" shapes (possible now that areas are editable
        // polygons) this can place waypoints inside solid geometry or miss a
        // legitimate concave passage. Acceptable for now since PathAi is only
        // used by the debug TestEntity - revisit if pathfinding ever gets wired
        // into real enemies.
        for (let obstacle of this.obstacles) {
            let bounds = polygonBounds(obstacle);
            this.addNode(bounds.minX - this.margin, bounds.minY - this.margin);
            this.addNode(bounds.maxX + this.margin, bounds.minY - this.margin);
            this.addNode(bounds.minX - this.margin, bounds.maxY + this.margin);
            this.addNode(bounds.maxX + this.margin, bounds.maxY + this.margin);
        }
    }

    isNodeValid(x: number, y: number){
        for (let obstacle of this.obstacles) {
            if (pointInPolygon(x, y, obstacle)) {
                return false;
            }
        }

        //return if node already exists
        for (let node of this.nodes) {
            if (node.x == x && node.y == y) {
                return false;
            }
        }

        return true;
    }

    addNode(x: number, y: number) {
        //return if inside an obstacle polygon
        if(!this.isNodeValid(x, y)){
            return;
        }

        this.nodes.push(new PathNode(x, y));
    }

    lineIntersectsObstacles(x1: number, y1: number, x2: number, y2: number): boolean {
        // Check if the line (x1, y1) -> (x2, y2) intersects with any obstacle
        for (let obstacle of this.obstacles) {
            if (lineIntersectsPolygon(x1, y1, x2, y2, obstacle)) {
                return true;
            }
        }
        return false;
    }

    connectNodes(maxNodeConnectionDistance: number) {
        this.edges = [];
        for (let i = 0; i < this.nodes.length; i++) {
            for (let j = i + 1; j < this.nodes.length; j++) {
                const nodeA = this.nodes[i];
                const nodeB = this.nodes[j];
                let d = p5js.dist(nodeA.x, nodeA.y, nodeB.x, nodeB.y);
                if (d <= maxNodeConnectionDistance) {
                    let validConnection = true;
                    for (let obstacle of this.obstacles) {
                        if (lineIntersectsPolygon(nodeA.x, nodeA.y, nodeB.x, nodeB.y, obstacle)) {
                            validConnection = false;
                            break;
                        }
                    }
                    if (validConnection) {
                        this.edges.push(new Edge(nodeA, nodeB, d));
                    }
                }
            }
        }
    }

    dijkstra(graph: Graph, startNode: PathNode, targetNode: PathNode): PathNode[] | null {
        const distances: Map<PathNode, number> = new Map();
        const previousNodes: Map<PathNode, PathNode | null> = new Map();
        const priorityQueue: { node: PathNode, priority: number }[] = [];
    
        // Initialize distances and priority queue
        this.graph.adjacencyList.forEach((_, node) => {
            distances.set(node, Infinity);
            previousNodes.set(node, null);
            priorityQueue.push({ node, priority: Infinity });
        });
    
        // Set the distance for the start node to 0
        distances.set(startNode, 0);
        priorityQueue.push({ node: startNode, priority: 0 });
    
        while (priorityQueue.length > 0) {
            // Get the node with the lowest priority (shortest distance)
            priorityQueue.sort((a, b) => a.priority - b.priority);
            const { node: currentNode } = priorityQueue.shift()!;
    
            // If we reached the target node, reconstruct the path
            if (currentNode === targetNode) {
                const path: PathNode[] = [];
                let current = targetNode;
                while (current !== null) {
                    path.unshift(current);
                    current = previousNodes.get(current)!;
                }
                return path;
            }
    
            // Update the distances and previous nodes for the neighbors
            for (const { neighbor, weight } of graph.getNeighbors(currentNode)) {
                const alt = distances.get(currentNode)! + weight;
                if (alt < distances.get(neighbor)!) {
                    distances.set(neighbor, alt);
                    previousNodes.set(neighbor, currentNode);
    
                    // Update the priority queue
                    priorityQueue.push({ node: neighbor, priority: alt });
                }
            }
        }
    
        // If the target node is unreachable
        return null;
    }

    renderDebug() {
        //calculate player map offset

        //draw obstacles
        for (let obstacle of this.obstacles) {
            buffer.noFill();
            buffer.stroke(255);
            buffer.strokeWeight(1);
            buffer.beginShape();
            for (let node of obstacle) {
                buffer.vertex(node.x-player.x, node.y-player.y);
            }
            buffer.endShape(p5js.CLOSE);
        }

        //draw edges
        // for (let edge of this.edges) {
        //     let posOnScreenA = {x: edge.nodeA.x-player.x, y: edge.nodeA.y-player.y};
        //     let posOnScreenB = {x: edge.nodeB.x-player.x, y: edge.nodeB.y-player.y};
        //     buffer.stroke(255, 50);
        //     buffer.strokeWeight(1);
        //     buffer.line(posOnScreenA.x, posOnScreenA.y, posOnScreenB.x, posOnScreenB.y);
        // }

        //draw path
        for (let i = 0; i < this.path.length - 1; i++) {
            let posOnScreenA = {x: this.path[i].x-player.x, y: this.path[i].y-player.y};
            let posOnScreenB = {x: this.path[i+1].x-player.x, y: this.path[i+1].y-player.y};
            buffer.stroke(0, 255, 0);
            buffer.strokeWeight(2);
            buffer.line(posOnScreenA.x, posOnScreenA.y, posOnScreenB.x, posOnScreenB.y);
        }

        // //draw nodes
        for (let node of this.path) {
            let posOnScreen = {x: node.x-player.x, y: node.y-player.y};

            if(node.explored){
                buffer.fill(0, 255, 0);
            }else{
                buffer.fill(255);
            }
            buffer.stroke(0);
            buffer.strokeWeight(2);
            buffer.ellipse(posOnScreen.x, posOnScreen.y, 6, 6);
        }

        
    }

    // drawLine45(x1: number, y1: number, x2: number, y2: number) {
    //     let minDist = this.findMidPoint45(x1, y1, x2, y2);      

    //     // buffer.line(x1, y1, mixX, mixY);
    //     // buffer.line(mixX, mixY, x2, y2);

    //     buffer.line(x1, y1, minDist.x, minDist.y);
    //     buffer.line(minDist.x, minDist.y, x2, y2);

    // }

    // findMidPoint45(x1: number, y1: number, x2: number, y2: number) {
    //     let minDist = p5js.min(p5js.abs(x2-x1), p5js.abs(y2-y1));

    //     let mixX = x1 + minDist;
    //     let mixY = y1 + minDist;

    //     if(x1 > x2){
    //         mixX = x1 - minDist;
    //     }
    //     if(y1 > y2){
    //         mixY = y1 - minDist;
    //     }

    //     return {x: mixX, y: mixY};
    // }
}

export default PathAi;